
import { useState, useEffect } from 'react';
import VideoCardWrapper from '@/components/layout/VideoCardWrapper';
import { Video } from '@/lib/api';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { usePlaylistContext } from '@/contexts/PlaylistContext';

interface RecommendedVideosProps {
  videos: Video[];
  onAddToPlaylist: (videoId: string, playlistId: string) => void;
  onToggleFavorite: (videoId: string) => void;
}

const RecommendedVideos = ({ 
  videos, 
  onAddToPlaylist, 
  onToggleFavorite 
}: RecommendedVideosProps) => {
  const [recommendedVideos, setRecommendedVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  // Utiliser le contexte pour les données de playlists et favoris
  const { favorites, playlists } = usePlaylistContext();

  useEffect(() => {
    const generateAdvancedRecommendations = async () => {
      if (!videos.length) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Validate and clean video data
        const validVideos = videos.filter(video => 
          video && 
          video.id && 
          video.title && 
          video.thumbnail && 
          video.channelName &&
          video.channelId &&
          video.publishedDate
        );

        if (validVideos.length === 0) {
          setLoading(false);
          return;
        }

        if (!user) {
          // Not logged in - use advanced trending algorithm
          const trendingVideos = getAdvancedTrendingVideos(validVideos);
          setRecommendedVideos(trendingVideos.slice(0, 10));
          setLoading(false);
          return;
        }
        
        // Get comprehensive user data
        const [profileData, favoritesData, playlistData, userVideosData] = await Promise.all([
          supabase.from('profiles').select('subscriptions').eq('id', user.id).maybeSingle(),
          supabase.from('favorites').select('video_id, created_at').eq('user_id', user.id),
          supabase.from('playlist_items').select('video_id, created_at').eq('user_id', user.id),
          supabase.from('user_videos').select('*').eq('added_by_user_id', user.id).limit(50)
        ]);

        // Extract interaction data with timestamps
        const favoriteVideoIds = favoritesData.data?.map(f => f.video_id) || [];
        const playlistVideoIds = playlistData.data?.map(p => p.video_id) || [];
        const favoriteTimestamps = favoritesData.data?.map(f => f.created_at) || [];
        const playlistTimestamps = playlistData.data?.map(p => p.created_at) || [];
        
        // Analyze user's historical video data
        const userHistoricalVideos = userVideosData.data || [];
        
        let personalizedVideos: Video[];

        if (!profileData?.data?.subscriptions?.length && favoriteVideoIds.length === 0) {
          // New user - use content-based filtering with diversity
          personalizedVideos = getNewUserRecommendations(validVideos);
        } else {
          // Get creators data if available
          let creatorsData: any[] = [];
          if (profileData?.data?.subscriptions?.length) {
            const { data } = await supabase
              .from('creators')
              .select('*')
              .in('creator_id', profileData.data.subscriptions);
            creatorsData = data || [];
          }

          // Advanced AI-powered recommendations
          personalizedVideos = getAIRecommendations(
            validVideos,
            creatorsData,
            favoriteVideoIds,
            playlistVideoIds,
            favoriteTimestamps,
            playlistTimestamps,
            userHistoricalVideos
          );
        }
        
        setRecommendedVideos(personalizedVideos.slice(0, 10));
      } catch (error) {
        console.error('Error generating advanced recommendations:', error);
        // Fallback to advanced trending
        const validVideos = videos.filter(video => 
          video && video.id && video.title && video.thumbnail
        );
        const trendingVideos = getAdvancedTrendingVideos(validVideos);
        setRecommendedVideos(trendingVideos.slice(0, 10));
      } finally {
        setLoading(false);
      }
    };

    generateAdvancedRecommendations();
  }, [videos, user, favorites, playlists]);

  // Advanced trending algorithm with multiple factors
  const getAdvancedTrendingVideos = (allVideos: Video[]): Video[] => {
    return allVideos
      .map(video => {
        const daysOld = (Date.now() - new Date(video.publishedDate).getTime()) / (1000 * 60 * 60 * 24);
        const recencyScore = Math.max(0, 7 - daysOld) / 7; // Higher score for recent videos
        const viewScore = Math.log10((video.views || 0) + 1);
        const durationScore = getDurationScore(video.duration);
        
        const trendingScore = (viewScore * 0.4) + (recencyScore * 0.4) + (durationScore * 0.2);
        
        return { video, score: trendingScore };
      })
      .sort((a, b) => b.score - a.score)
      .map(item => item.video);
  };

  // Smart duration scoring (prefer 8-20 minute videos)
  const getDurationScore = (duration: string): number => {
    const minutes = parseDuration(duration);
    if (minutes >= 8 && minutes <= 20) return 1;
    if (minutes >= 5 && minutes <= 30) return 0.8;
    if (minutes >= 3 && minutes <= 60) return 0.6;
    return 0.4;
  };

  const parseDuration = (duration: string): number => {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;
    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    return hours * 60 + minutes;
  };

  // AI-powered comprehensive recommendation system
  const getAIRecommendations = (
    allVideos: Video[],
    creatorsData: any[],
    favoriteVideoIds: string[],
    playlistVideoIds: string[],
    favoriteTimestamps: string[],
    playlistTimestamps: string[],
    userHistoricalVideos: any[]
  ): Video[] => {
    // 1. Temporal analysis - understand user behavior patterns
    const temporalPreferences = analyzeTemporalPatterns(favoriteTimestamps, playlistTimestamps);
    
    // 2. Content analysis - deep dive into user's content preferences
    const contentProfile = buildContentProfile(
      allVideos, 
      favoriteVideoIds, 
      playlistVideoIds, 
      userHistoricalVideos
    );
    
    // 3. Creator affinity analysis
    const creatorAffinities = analyzeCreatorAffinities(creatorsData, allVideos, favoriteVideoIds);
    
    // 4. Diversity and exploration factors
    const diversityFactors = calculateDiversityNeeds(contentProfile);
    
    // 5. Advanced scoring with multiple ML-like factors
    const scoredVideos = allVideos
      .filter(video => !favoriteVideoIds.includes(video.id) && !playlistVideoIds.includes(video.id))
      .map(video => {
        const contentScore = calculateContentScore(video, contentProfile);
        const creatorScore = calculateCreatorScore(video, creatorAffinities);
        const temporalScore = calculateTemporalScore(video, temporalPreferences);
        const diversityScore = calculateDiversityScore(video, diversityFactors);
        const qualityScore = calculateQualityScore(video);
        const freshnessScore = calculateFreshnessScore(video);
        
        // Weighted combination of all factors
        const finalScore = 
          contentScore * 0.25 +
          creatorScore * 0.20 +
          temporalScore * 0.15 +
          diversityScore * 0.15 +
          qualityScore * 0.15 +
          freshnessScore * 0.10;
          
        return { video, score: finalScore, contentScore, creatorScore, diversityScore };
      })
      .sort((a, b) => b.score - a.score);

    // 6. Apply diversity filtering to final results
    return applyDiversityFiltering(scoredVideos);
  };

  // Analyze when user is most active and what type of content they prefer at different times
  const analyzeTemporalPatterns = (favoriteTimestamps: string[], playlistTimestamps: string[]) => {
    const allTimestamps = [...favoriteTimestamps, ...playlistTimestamps];
    const hourPreferences: Record<number, number> = {};
    const dayPreferences: Record<number, number> = {};
    
    allTimestamps.forEach(timestamp => {
      const date = new Date(timestamp);
      const hour = date.getHours();
      const day = date.getDay();
      
      hourPreferences[hour] = (hourPreferences[hour] || 0) + 1;
      dayPreferences[day] = (dayPreferences[day] || 0) + 1;
    });
    
    return { hourPreferences, dayPreferences };
  };

  // Build comprehensive content profile using NLP-like techniques
  const buildContentProfile = (
    allVideos: Video[], 
    favoriteVideoIds: string[], 
    playlistVideoIds: string[], 
    userHistoricalVideos: any[]
  ) => {
    const userVideos = allVideos.filter(v => 
      favoriteVideoIds.includes(v.id) || playlistVideoIds.includes(v.id)
    );
    
    // Enhanced keyword extraction and weighting
    const keywordWeights: Record<string, number> = {};
    const categoryWeights: Record<string, number> = {};
    const creatorWeights: Record<string, number> = {};
    const durationPreferences: number[] = [];
    
    [...userVideos, ...userHistoricalVideos].forEach(video => {
      // Extract keywords from title and channel
      const text = `${video.title} ${video.channelName || video.channel_name}`.toLowerCase();
      const words = text.split(/\W+/).filter(word => word.length > 3);
      
      words.forEach(word => {
        keywordWeights[word] = (keywordWeights[word] || 0) + 1;
      });
      
      // Track creator preferences
      const creatorId = video.channelId || video.channel_id;
      if (creatorId) {
        creatorWeights[creatorId] = (creatorWeights[creatorId] || 0) + 1;
      }
      
      // Track duration preferences
      const duration = parseDuration(video.duration);
      if (duration > 0) durationPreferences.push(duration);
      
      // Advanced category detection
      const categories = detectAdvancedCategories(text);
      categories.forEach(category => {
        categoryWeights[category] = (categoryWeights[category] || 0) + 1;
      });
    });
    
    return { keywordWeights, categoryWeights, creatorWeights, durationPreferences };
  };

  // Advanced category detection with more nuanced understanding
  const detectAdvancedCategories = (text: string): string[] => {
    const categories: string[] = [];
    
    const categoryPatterns = {
      gaming: ['game', 'gaming', 'play', 'gamer', 'stream', 'twitch', 'esports', 'minecraft', 'fortnite', 'valorant', 'league'],
      tech: ['tech', 'technology', 'review', 'unboxing', 'iphone', 'android', 'laptop', 'programming', 'coding', 'ai', 'software'],
      education: ['tutorial', 'learn', 'how to', 'course', 'education', 'explained', 'guide', 'tips', 'study', 'lesson'],
      entertainment: ['funny', 'comedy', 'meme', 'react', 'reaction', 'prank', 'challenge', 'vlog', 'behind'],
      music: ['music', 'song', 'artist', 'album', 'concert', 'live', 'cover', 'remix', 'beat', 'lyrics'],
      lifestyle: ['lifestyle', 'daily', 'routine', 'morning', 'skincare', 'fitness', 'workout', 'health', 'motivation'],
      cooking: ['recipe', 'cooking', 'food', 'chef', 'kitchen', 'baking', 'meal', 'taste', 'restaurant'],
      travel: ['travel', 'trip', 'vacation', 'country', 'city', 'culture', 'adventure', 'explore', 'journey'],
      news: ['news', 'politics', 'world', 'breaking', 'report', 'analysis', 'economy', 'election'],
      science: ['science', 'physics', 'space', 'research', 'experiment', 'discovery', 'nasa', 'climate'],
      sports: ['sports', 'football', 'basketball', 'soccer', 'nba', 'nfl', 'olympics', 'match', 'team'],
      business: ['business', 'entrepreneur', 'startup', 'investment', 'money', 'finance', 'marketing', 'success']
    };
    
    Object.entries(categoryPatterns).forEach(([category, keywords]) => {
      if (keywords.some(keyword => text.includes(keyword))) {
        categories.push(category);
      }
    });
    
    return categories;
  };

  // Analyze creator affinity based on interaction patterns
  const analyzeCreatorAffinities = (creatorsData: any[], allVideos: Video[], favoriteVideoIds: string[]) => {
    const affinities: Record<string, number> = {};
    
    // Direct subscriptions
    creatorsData.forEach(creator => {
      affinities[creator.creator_id] = 3; // Base subscription weight
    });
    
    // Interaction-based affinities
    const favoriteVideos = allVideos.filter(v => favoriteVideoIds.includes(v.id));
    favoriteVideos.forEach(video => {
      affinities[video.channelId] = (affinities[video.channelId] || 0) + 2;
    });
    
    return affinities;
  };

  // Calculate what type of diversity the user needs
  const calculateDiversityNeeds = (contentProfile: any) => {
    const topCategories = Object.entries(contentProfile.categoryWeights)
      .sort((a, b) => (b[1] as number) - (a[1] as number))
      .slice(0, 3)
      .map(([category]) => category);
    
    return {
      needsCategoryDiversity: topCategories.length < 3,
      dominantCategories: topCategories,
      needsCreatorDiversity: Object.keys(contentProfile.creatorWeights).length < 5
    };
  };

  // Sophisticated content scoring
  const calculateContentScore = (video: Video, contentProfile: any): number => {
    const text = `${video.title} ${video.channelName}`.toLowerCase();
    let score = 0;
    
    // Keyword matching with TF-IDF-like weighting
    const words = text.split(/\W+/).filter(word => word.length > 3);
    words.forEach(word => {
      if (contentProfile.keywordWeights[word]) {
        score += Math.log(contentProfile.keywordWeights[word] + 1);
      }
    });
    
    // Category matching
    const categories = detectAdvancedCategories(text);
    categories.forEach(category => {
      if (contentProfile.categoryWeights[category]) {
        score += contentProfile.categoryWeights[category] * 0.5;
      }
    });
    
    return score;
  };

  const calculateCreatorScore = (video: Video, creatorAffinities: Record<string, number>): number => {
    return creatorAffinities[video.channelId] || 0;
  };

  const calculateTemporalScore = (video: Video, temporalPreferences: any): number => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay();
    
    const hourScore = temporalPreferences.hourPreferences[currentHour] || 0;
    const dayScore = temporalPreferences.dayPreferences[currentDay] || 0;
    
    return (hourScore + dayScore) * 0.1;
  };

  const calculateDiversityScore = (video: Video, diversityFactors: any): number => {
    const text = `${video.title} ${video.channelName}`.toLowerCase();
    const categories = detectAdvancedCategories(text);
    
    let diversityScore = 0;
    
    // Reward content from underrepresented categories
    if (diversityFactors.needsCategoryDiversity) {
      const isFromDominantCategory = categories.some(cat => 
        diversityFactors.dominantCategories.includes(cat)
      );
      if (!isFromDominantCategory) diversityScore += 1;
    }
    
    return diversityScore;
  };

  const calculateQualityScore = (video: Video): number => {
    const views = video.views || 0;
    const daysOld = (Date.now() - new Date(video.publishedDate).getTime()) / (1000 * 60 * 60 * 24);
    
    // Quality indicators
    const viewsPerDay = views / Math.max(daysOld, 1);
    const durationScore = getDurationScore(video.duration);
    
    return Math.log10(viewsPerDay + 1) * 0.3 + durationScore * 0.7;
  };

  const calculateFreshnessScore = (video: Video): number => {
    const daysOld = (Date.now() - new Date(video.publishedDate).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, (7 - daysOld) / 7); // Prefer videos from last week
  };

  // Apply diversity filtering to ensure varied recommendations
  const applyDiversityFiltering = (scoredVideos: any[]): Video[] => {
    const result: Video[] = [];
    const usedCreators = new Set<string>();
    const usedCategories = new Set<string>();
    
    for (const item of scoredVideos) {
      if (result.length >= 10) break;
      
      const video = item.video;
      const categories = detectAdvancedCategories(`${video.title} ${video.channelName}`.toLowerCase());
      
      // Ensure diversity in creators and categories
      const creatorUsed = usedCreators.has(video.channelId);
      const categoryOverlap = categories.some(cat => usedCategories.has(cat));
      
      if (!creatorUsed || (!categoryOverlap && usedCreators.size < 3)) {
        result.push(video);
        usedCreators.add(video.channelId);
        categories.forEach(cat => usedCategories.add(cat));
      } else if (result.length < 6) {
        // Allow some repetition for top-scored items
        result.push(video);
      }
    }
    
    return result;
  };

  // Recommendations for new users without interaction history
  const getNewUserRecommendations = (allVideos: Video[]): Video[] => {
    // Use a mix of trending, diverse content, and educational material
    const trending = getAdvancedTrendingVideos(allVideos).slice(0, 4);
    
    // Add diverse category representation
    const categoryVideos: Record<string, Video[]> = {};
    allVideos.forEach(video => {
      const categories = detectAdvancedCategories(`${video.title} ${video.channelName}`.toLowerCase());
      categories.forEach(category => {
        if (!categoryVideos[category]) categoryVideos[category] = [];
        categoryVideos[category].push(video);
      });
    });
    
    const diverse: Video[] = [];
    Object.entries(categoryVideos).forEach(([category, videos]) => {
      if (diverse.length < 6) {
        const categoryBest = videos
          .sort((a, b) => (b.views || 0) - (a.views || 0))
          .slice(0, 1);
        diverse.push(...categoryBest);
      }
    });
    
    return [...trending, ...diverse].slice(0, 10);
  };

  if (videos.length === 0) return null;
  
  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold mb-4">Vidéos recommandées pour vous</h2>
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-youtube-red"></div>
        </div>
      ) : recommendedVideos.length > 0 ? (
        <div className="video-grid">
          {recommendedVideos.map((video) => (
            <VideoCardWrapper
              key={video.id}
              {...video}
            />
          ))}
        </div>
      ) : (
        <p className="text-gray-500">Aucune recommandation disponible pour le moment</p>
      )}
    </div>
  );
};

export default RecommendedVideos;
